import { Injectable } from '@angular/core';
import { Firestore, collection, query, where, orderBy, limit, getDocs, DocumentData, FirestoreError, doc, deleteDoc } from '@angular/fire/firestore';
import { Observable, from, map, catchError, throwError, tap, of, retry, shareReplay } from 'rxjs';
import { News } from '../models/news.interface';

@Injectable({
  providedIn: 'root'
})
export class NewsService {
  private readonly RETRY_ATTEMPTS = 3;
  private readonly RETRY_DELAY = 1000; // 1 segundo
  private readonly CACHE_SIZE = 1;
  private readonly CACHE_TIME = 5 * 60 * 1000; // 5 minutos

  private featuredArticlesCache$?: Observable<News[]>;
  private categoryCache = new Map<string, Observable<News[]>>();
  private lastCacheTime = new Map<string, number>();
  private allArticlesCache$?: Observable<News[]>;
  
  // Caché local para consultas de Firestore
  private firestoreCache = new Map<string, { data: any; timestamp: number }>();
  private readonly FIRESTORE_CACHE_DURATION = 3 * 60 * 1000; // 3 minutos

  constructor(private firestore: Firestore) {}

  private isCacheValid(cacheKey: string): boolean {
    const lastTime = this.lastCacheTime.get(cacheKey);
    return lastTime ? (Date.now() - lastTime) < this.CACHE_TIME : false;
  }

  private updateCacheTime(cacheKey: string): void {
    this.lastCacheTime.set(cacheKey, Date.now());
  }

  private handleError(error: FirestoreError) {
    // console.error('Firestore error:', error);
    let errorMessage = 'An error occurred while fetching the news';
    
    switch (error.code) {
      case 'failed-precondition':
        if (error.message.includes('index')) {
          errorMessage = 'Database indexing in progress. Please try again in a few minutes.';
        }
        break;
      case 'unavailable':
        errorMessage = 'Service temporarily unavailable. Please try again.';
        break;
      case 'not-found':
        errorMessage = 'Article not found';
        break;
      default:
        errorMessage = `Error: ${error.message}`;
    }
    
    return throwError(() => ({ code: error.code, message: errorMessage }));
  }

  // Get all articles without any filters first
  private getAllArticles(): Observable<News[]> {
    if (this.allArticlesCache$ && this.isCacheValid('all')) {
      return this.allArticlesCache$;
    }

    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('status', '==', 'published')
    );
    
    this.allArticlesCache$ = from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => {
        if (snapshot.empty) {
          // console.log('No articles found');
          return [];
        }

        const articles = snapshot.docs.map(doc => {
          const data = doc.data();
          const publishDate = (data['publishDate'] && typeof data['publishDate'].toDate === 'function') ? data['publishDate'].toDate() : data['publishDate'];
          const lastModified = (data['lastModified'] && typeof data['lastModified'].toDate === 'function') ? data['lastModified'].toDate() : data['lastModified'];
          const createdAt = (data['createdAt'] && typeof data['createdAt'].toDate === 'function') ? data['createdAt'].toDate() : data['createdAt'];
          return { 
            id: doc.id,
            ...data,
            publishDate,
            lastModified,
            createdAt
          } as News;
        });

        articles.sort((a, b) => {
          const dateA = a.publishDate instanceof Date ? a.publishDate.getTime() : 0;
          const dateB = b.publishDate instanceof Date ? b.publishDate.getTime() : 0;
          return dateB - dateA;
        });

        return articles;
      }),
      tap(() => this.updateCacheTime('all')),
      shareReplay(this.CACHE_SIZE),
      catchError((error: FirestoreError) => this.handleError(error))
    );

    return this.allArticlesCache$;
  }

  // Get featured articles with optimized limit and local cache
  getFeaturedArticles(forceServerData: boolean = false): Observable<News[]> {
    const cacheKey = 'featured_articles_4';
    
    // Verificar caché local primero si no se fuerzan datos del servidor
    if (!forceServerData) {
      const cached = this.getFromFirestoreCache(cacheKey);
      if (cached) {
        return of(cached);
      }
    }
    
    if (!forceServerData && this.featuredArticlesCache$ && this.isCacheValid('featured')) {
      return this.featuredArticlesCache$;
    }

    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('featured', '==', true),
      where('status', '==', 'published'),
      where('category', '!=', 'ciudad'),
      orderBy('publishDate', 'desc'),
      limit(4)
    );
    
    // Para forzar datos del servidor, limpiar caché primero
    if (forceServerData) {
      this.featuredArticlesCache$ = undefined;
      this.lastCacheTime.delete('featured');
      this.firestoreCache.delete(cacheKey);
    }
    
    this.featuredArticlesCache$ = from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => {
        if (snapshot.empty) return [];
        
        return snapshot.docs
          .map(doc => {
            const data = doc.data();
            const publishDate = (data['publishDate'] && typeof data['publishDate'].toDate === 'function') ? data['publishDate'].toDate() : data['publishDate'];
            const lastModified = (data['lastModified'] && typeof data['lastModified'].toDate === 'function') ? data['lastModified'].toDate() : data['lastModified'];
            const createdAt = (data['createdAt'] && typeof data['createdAt'].toDate === 'function') ? data['createdAt'].toDate() : data['createdAt'];
            return {
              id: doc.id,
              ...data,
              publishDate,
              lastModified,
              createdAt
            } as News;
          });
      }),
      tap((articles: News[]) => {
        this.updateCacheTime('featured');
        this.setFirestoreCache(cacheKey, articles);
      }),
      shareReplay(this.CACHE_SIZE),
      catchError((error: FirestoreError) => this.handleError(error))
    );

    return this.featuredArticlesCache$;
  }

  // Get featured articles excluding one specific article
  getFeaturedArticlesExcluding(excludeTitle: string): Observable<News[]> {
    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('featured', '==', true),
      where('status', '==', 'published'),
      where('category', '!=', 'ciudad'),
      limit(10) // Pedimos más artículos para tener suficientes después de filtrar
    );
    
    return from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => {
        if (snapshot.empty) return [];
        
        return snapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data(),
            publishDate: doc.data()['publishDate']?.toDate()
          } as News))
          .filter(article => article.title !== excludeTitle)
          .slice(0, 3); // Tomamos solo 3 artículos después de filtrar
      }),
      catchError((error: FirestoreError) => this.handleError(error))
    );
  }

  // Get articles by category with optimized limits and local cache
  getArticlesByCategory(category: string, limitCount: number = 4): Observable<News[]> {
    const cacheKey = `category_${category}_${limitCount}`;
    
    // Verificar caché local primero
    const cached = this.getFromFirestoreCache(cacheKey);
    if (cached) {
      return of(cached);
    }
    
    if (this.categoryCache.has(cacheKey) && this.isCacheValid(cacheKey)) {
      return this.categoryCache.get(cacheKey)!;
    }

    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('category', '==', category),
      where('status', '==', 'published'),
      orderBy('publishDate', 'desc'),
      limit(limitCount)
    );

    const categoryNews$ = from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => {
        if (snapshot.empty) return [];
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          const publishDate = (data['publishDate'] && typeof data['publishDate'].toDate === 'function') ? data['publishDate'].toDate() : data['publishDate'];
          const lastModified = (data['lastModified'] && typeof data['lastModified'].toDate === 'function') ? data['lastModified'].toDate() : data['lastModified'];
          const createdAt = (data['createdAt'] && typeof data['createdAt'].toDate === 'function') ? data['createdAt'].toDate() : data['createdAt'];
          return {
            id: doc.id,
            ...data,
            publishDate,
            lastModified,
            createdAt
          } as News;
        });
      }),
      tap((articles: News[]) => {
        this.updateCacheTime(cacheKey);
        this.setFirestoreCache(cacheKey, articles);
      }),
      shareReplay(this.CACHE_SIZE),
      catchError((error: FirestoreError) => this.handleError(error))
    );

    this.categoryCache.set(cacheKey, categoryNews$);
    return categoryNews$;
  }

  // Get latest articles with optimized limit and local cache
  getLatestArticles(limitCount: number = 4): Observable<News[]> {
    const cacheKey = `latest_${limitCount}`;
    
    // Verificar caché local primero
    const cached = this.getFromFirestoreCache(cacheKey);
    if (cached) {
      return of(cached);
    }
    
    if (this.categoryCache.has(cacheKey) && this.isCacheValid(cacheKey)) {
      return this.categoryCache.get(cacheKey)!;
    }

    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('status', '==', 'published'),
      orderBy('publishDate', 'desc'),
      limit(limitCount)
    );

    const latestNews$ = from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => {
        if (snapshot.empty) return [];
        
        return snapshot.docs.map(doc => {
          const data = doc.data();
          const publishDate = (data['publishDate'] && typeof data['publishDate'].toDate === 'function') ? data['publishDate'].toDate() : data['publishDate'];
          const lastModified = (data['lastModified'] && typeof data['lastModified'].toDate === 'function') ? data['lastModified'].toDate() : data['lastModified'];
          const createdAt = (data['createdAt'] && typeof data['createdAt'].toDate === 'function') ? data['createdAt'].toDate() : data['createdAt'];
          return {
            id: doc.id,
            ...data,
            publishDate,
            lastModified,
            createdAt
          } as News;
        });
      }),
      tap((articles: News[]) => {
        this.updateCacheTime(cacheKey);
        this.setFirestoreCache(cacheKey, articles);
      }),
      shareReplay(this.CACHE_SIZE),
      catchError((error: FirestoreError) => this.handleError(error))
    );

    this.categoryCache.set(cacheKey, latestNews$);
    return latestNews$;
  }

  // Get article by slug
  getArticleBySlug(slug: string): Observable<News> {
    if (!slug) {
      return throwError(() => new Error('Slug is required'));
    }

    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('slug', '==', slug),
      where('status', '==', 'published'),
      limit(1)
    );

    return from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => {
        if (snapshot.empty) {
          throw new Error('Article not found');
        }

        const doc = snapshot.docs[0];
        const data = doc.data();
        const publishDate = (data['publishDate'] && typeof data['publishDate'].toDate === 'function') ? data['publishDate'].toDate() : data['publishDate'];
        const lastModified = (data['lastModified'] && typeof data['lastModified'].toDate === 'function') ? data['lastModified'].toDate() : data['lastModified'];
        const createdAt = (data['createdAt'] && typeof data['createdAt'].toDate === 'function') ? data['createdAt'].toDate() : data['createdAt'];

        return {
          id: doc.id,
          ...data,
          publishDate,
          lastModified,
          createdAt
        } as News;
      }),
      catchError(error => {
        console.error('Error fetching article:', error);
        return throwError(() => error);
      })
    );
  }

  // Get related articles
  getRelatedArticles(category: string, excludeId?: string, limitCount: number = 4): Observable<News[]> {
    if (!category) {
      return of([]);
    }

    return this.getAllArticles().pipe(
      map(articles => {
        return articles
          .filter(article => 
            article.category?.toLowerCase() === category.toLowerCase() && 
            article.id !== excludeId
          )
          .slice(0, limitCount);
      })
    );
  }

  // Get latest news
  getLatestNews(excludeId?: string, limitCount: number = 5): Observable<News[]> {
    return this.getAllArticles().pipe(
      map(articles => {
        return articles
          .filter(article => article.id !== excludeId)
          .slice(0, limitCount);
      })
    );
  }

  // Get main news
  getMainNews(forceServerData: boolean = false): Observable<News[]> {
    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('status', '==', 'published'),
      where('category', '==', 'principales'),
      orderBy('publishedAt', 'desc'),
      limit(3)
    );
    
    // Para forzar datos del servidor, no usar caché
    if (forceServerData) {
      return from(getDocs(q)).pipe(
        retry({
          count: this.RETRY_ATTEMPTS,
          delay: this.RETRY_DELAY
        }),
        map(snapshot => 
          snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          } as News))
        ),
        catchError(error => this.handleError(error))
      );
    }
    
    return from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => 
        snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as News))
      ),
      catchError(error => this.handleError(error))
    );
  }

  // Clear featured articles cache
  clearFeaturedCache(): void {
    this.featuredArticlesCache$ = undefined;
    this.lastCacheTime.delete('featured');
    // console.log('Featured articles cache cleared');
  }

  /**
   * Limpia específicamente la caché del banner para forzar la recarga de datos frescos
   * Útil cuando se detectan cambios en las imágenes del banner
   */
  clearBannerCache(): void {
    this.featuredArticlesCache$ = undefined;
    this.lastCacheTime.delete('featured');
    this.firestoreCache.delete('featured_articles_4');
    // console.log('Banner cache cleared - forcing fresh data from server');
  }

  /**
   * Obtiene datos desde caché local de Firestore si son válidos
   */
  private getFromFirestoreCache(key: string): any | null {
    const cached = this.firestoreCache.get(key);
    if (cached && (Date.now() - cached.timestamp) < this.FIRESTORE_CACHE_DURATION) {
      return cached.data;
    }
    
    // Limpiar caché expirado
    if (cached) {
      this.firestoreCache.delete(key);
    }
    
    return null;
  }

  /**
   * Guarda datos en caché local de Firestore
   */
  private setFirestoreCache(key: string, data: any): void {
    this.firestoreCache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  /**
   * Limpia caché de Firestore expirado
   */
  clearExpiredFirestoreCache(): void {
    const now = Date.now();
    for (const [key, value] of this.firestoreCache.entries()) {
      if ((now - value.timestamp) >= this.FIRESTORE_CACHE_DURATION) {
        this.firestoreCache.delete(key);
      }
    }
  }

  /**
   * Limpia toda la caché de Firestore
   */
  clearAllFirestoreCache(): void {
    this.firestoreCache.clear();
  }

  // Get total count of published articles for edition number
  getTotalNewsCount(): Observable<number> {
    const articlesRef = collection(this.firestore, 'articles');
    const q = query(
      articlesRef,
      where('status', '==', 'published')
    );

    return from(getDocs(q)).pipe(
      retry({
        count: this.RETRY_ATTEMPTS,
        delay: this.RETRY_DELAY
      }),
      map(snapshot => snapshot.size),
      catchError((error: FirestoreError) => {
        console.error('Error getting news count:', error);
        return of(0); // Return 0 if there's an error
      })
    );
  }

  // Clear all caches
  clearAllCaches(): void {
    this.allArticlesCache$ = undefined;
    this.featuredArticlesCache$ = undefined;
    this.categoryCache.clear();
    this.lastCacheTime.clear();
    this.firestoreCache.clear();
    // console.log('All caches cleared');
  }

  // Delete article by ID
  deleteArticle(id: string): Observable<void> {
    if (!id) {
      return throwError(() => new Error('Article ID is required for deletion'));
    }
    const articleDocRef = doc(this.firestore, `articles/${id}`);
    return from(deleteDoc(articleDocRef)).pipe(
      tap(() => {
        // Invalidar cachés relevantes si es necesario después de eliminar
        this.clearAllCaches();
        // console.log(`Article with ID ${id} deleted successfully.`);
      }),
      catchError(error => {
        // console.error(`Error deleting article with ID ${id}:`, error);
        // Usar handleError para consistencia, aunque deleteDoc no suele devolver FirestoreError específicas como las de getDocs
        return throwError(() => new Error(`Failed to delete article ${id}. Error: ${error.message || error}`)); 
      })
    );
  }
}
